import React, {
    CSSProperties,
    ChangeEventHandler,
    InputHTMLAttributes,
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import styles from './styles.module.scss';
import { useFallbackId } from '../../useFallbackId';
import { Label } from '../Label';
import { RequiredStrategy } from '../../Form';

export type TextAreaProps = {
    label: string;
    name: string;
    height?: number;
    requiredStrategy?: RequiredStrategy;
} & Omit<InputHTMLAttributes<HTMLTextAreaElement>, 'name' | 'placeholder'>;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    (
        {
            id,
            name,
            label,
            onChange,
            height,
            style = {},
            required = false,
            requiredStrategy = 'required',
            ...props
        },
        ref
    ) => {
        const inputId = useFallbackId(id);
        const [controlledHeight, setControlledHeight] = useState<number | undefined>(undefined);
        const [minimumHeight, setMinimumHeight] = useState<number | undefined>(undefined);
        const htmlTextAreaRef = useRef<HTMLTextAreaElement>();
        const fauxTextAreaRef = useRef<HTMLDivElement>(null);

        /**
         * Expose native textarea while also referencing the element internally.
         */
        const proxyRef = useCallback(
            (element: HTMLTextAreaElement) => {
                if (ref) {
                    if ('current' in ref) {
                        ref.current = element;
                    } else {
                        ref(element);
                    }
                }

                htmlTextAreaRef.current = element;
            },
            [ref, htmlTextAreaRef]
        );

        const resizeNativeInput = useCallback((targetHeight: number) => {
            setControlledHeight(targetHeight);
        }, []);

        const _onChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
            (event) => {
                resizeNativeInput(event.target.scrollHeight);

                return onChange?.(event);
            },
            [onChange, htmlTextAreaRef]
        );

        useEffect(() => {
            if (minimumHeight) {
                resizeNativeInput(minimumHeight);
            }
        }, [minimumHeight]);

        useEffect(() => {
            if (!fauxTextAreaRef.current) {
                return;
            }

            const boundingRect = fauxTextAreaRef.current.getBoundingClientRect();
            const { height: boundingHeight } = boundingRect;

            setMinimumHeight(boundingHeight);
        }, [fauxTextAreaRef]);

        const textAreaStyles = useMemo<CSSProperties>(() => {
            /**
             * The textarea must be at least the height of the faux text area
             * so the user can focus the invisible textarea by clicking
             * anywhere within the boundary.
             */
            if (controlledHeight) {
                const heightStyle = { height: Math.max(controlledHeight, minimumHeight ?? 0) };
                const minHeightStyle = { minHeight: minimumHeight };
                const internalStyles = { ...heightStyle, ...minHeightStyle };

                return { ...style, ...internalStyles };
            }

            return style;
        }, [controlledHeight, minimumHeight, props]);

        return (
            <div className={styles.textAreaContainer}>
                <div className={styles.fauxTextArea} ref={fauxTextAreaRef}>
                    <div className={styles.fauxTextAreaInner} style={{ height: height }}>
                        <textarea
                            ref={proxyRef}
                            id={inputId}
                            name={name}
                            placeholder={label}
                            onChange={_onChange}
                            style={textAreaStyles}
                            required={required}
                            {...props}
                        />

                        <Label
                            htmlFor={inputId}
                            required={required}
                            requiredStrategy={requiredStrategy}
                        >
                            {label}
                        </Label>
                    </div>
                </div>
            </div>
        );
    }
);
